import React from 'react';
import type { ElementType } from 'react';
import {
    Breadcrumb,
    Grid,
    useAppDisplayModeContext,
    useTheme,
    pxToRem,
    useStyles,
} from '@archipro-design/aria';
import * as S from './PageHeader.style';
import VariantPricing from '@modules/product/component/variant-pricing/VariantPricing';
import useVariantPrice from '@modules/product/hook/use-variant-price';
import { Link } from '@remix-run/react';
import { superscriptize } from '~/utils';
import { useVariantSelectionState } from '@modules/product/context/VariantSelectionContext';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import PageDetailHeaderCTA from './page-detail-header-cta/PageDetailHeaderCTA';

import { ProfessionalAvatar } from '~/ui-rocco/pages/shared/ProfessionalAvatar';

interface PageHeaderProps {
    data: {
        Breadcrumbs: ProductPageLoaderData['product']['Breadcrumbs'];
        ID: ProductPageLoaderData['product']['ID'];
        Title: ProductPageLoaderData['product']['Title'];
        Pricing: ProductPageLoaderData['product']['Pricing'];
        Professional: Pick<
            ProductPageLoaderData['product']['Professional'],
            | 'Title'
            | 'Link'
            | 'Country'
            | 'PricingDisabled'
            | 'Website'
            | 'PickupLocations'
            | 'CompanyLogo'
            | 'LogoBackgroundColor'
        >;
        Website?: ProductPageLoaderData['product']['Website'];
        EcommerceReady?: boolean;
        PrimaryCTA: ProductPageLoaderData['primaryCTA'];
    };
    isSticky?: boolean;
    openEnquiryModal: (changeUrl?: boolean) => void;
}

const HIDE_CTA_AND_CONTENT = true;

const PageHeader = React.forwardRef<HTMLDivElement, PageHeaderProps>(
    function PageHeader(
        {
            data: {
                Breadcrumbs,
                ID,
                Title,
                Pricing,
                Professional,
                Website,
                PrimaryCTA,
            },
            isSticky = false,
            openEnquiryModal,
        },
        ref
    ) {
        const { mobile } = useAppDisplayModeContext();
        const { selectedVariant } = useVariantSelectionState();

        const theme = useTheme();
        const { hasPrice: variantHasPrice } = useVariantPrice(
            Pricing.PricingType
        );
        const breadcrumbColor = theme.siteVariables.colors.grey[300];
        const hasPrice = variantHasPrice && !Professional.PricingDisabled;
        const iconAdjustedTitle = superscriptize(Title);

        const userCanViewPrice = selectedVariant?.CanViewPrice;

        const { css } = useStyles({ isSticky });

        const shareItem = (
            <PageDetailHeaderCTA
                website={Website || undefined}
                pageID={ID}
                professionalTitle={Professional.Title}
                primaryCTAButtonLabel={
                    !mobile ? PrimaryCTA.primaryCTALabel : undefined
                }
                primaryCTAButtonOnClick={() => {
                    openEnquiryModal(false);
                }}
                pageType="Product"
            />
        );

        return (
            <div ref={ref} className={css(S.ProductHeaderContainer)}>
                <div className={css(S.PageHeader)}>
                    <div className="flex justify-between mt-auto pt-1.5 md:hidden">
                        {!isSticky && (
                            <Breadcrumb
                                logo={null}
                                home={{
                                    link: true,
                                    as: Link,
                                    label: 'Home',
                                    to: '/',
                                }}
                                items={Breadcrumbs.map((breadcrumb) => ({
                                    label: breadcrumb.label,
                                    href: breadcrumb.href,
                                    as: breadcrumb.as as ElementType,
                                    variables: {
                                        breadcrumbItemTextColor:
                                            breadcrumbColor,
                                    },
                                }))}
                                data-testid="breadcrumb-menu"
                                variables={{
                                    breadcrumbFontSize:
                                        theme.siteVariables.fontSizes.caption04,
                                }}
                            />
                        )}
                    </div>
                    <div className={css(S.TitleContainer)}>
                        <h1
                            className="mt-0 mb-0 text-5 leading-[1.1] md:text-20 md:leading-[1.1] md:tracking-[-2.4px] text-[#000000]"
                            dangerouslySetInnerHTML={{
                                __html: iconAdjustedTitle,
                            }}
                        />

                        <Link
                            to={Professional.Link}
                            className="mt-0 mb-0 text-4 leading-[1.1] md:text-16 md:leading-[1.1] md:tracking-[-2.4px] text-[#b6b6b6] hover:text-gray-400"
                        >
                            {Professional.Title}
                        </Link>

                        {!HIDE_CTA_AND_CONTENT && (
                            <Grid
                                columns={mobile ? '1fr' : `${pxToRem(880)} 1fr`}
                                className={css({
                                    columnGap: pxToRem(44),
                                    width: '100%',
                                    alignItems: 'center',
                                })}
                                design={{
                                    display: mobile ? 'block' : 'grid',
                                }}
                            >
                                <div className="flex mt-2 md:mt-0">
                                    <ProfessionalAvatar
                                        name={Professional.Title}
                                        logo={Professional.CompanyLogo}
                                        logoBackgroundColor={
                                            Professional.LogoBackgroundColor &&
                                            Professional.LogoBackgroundColor
                                                .length > 0
                                                ? Professional.LogoBackgroundColor
                                                : 'white'
                                        }
                                        profileLink={Professional.Link}
                                        avatarSize={
                                            mobile ? 'square24' : 'square32'
                                        }
                                    />
                                </div>

                                {!mobile && (
                                    <div className={css(S.HeaderCTA)}>
                                        <div>
                                            {hasPrice && userCanViewPrice && (
                                                <VariantPricing
                                                    pricingType={
                                                        Pricing.PricingType
                                                    }
                                                />
                                            )}
                                        </div>
                                        <div>{shareItem}</div>
                                    </div>
                                )}
                            </Grid>
                        )}
                    </div>
                </div>
            </div>
        );
    }
);

export default PageHeader;
